<template>
  <div>
    <main class="SearchList w-100">
      <div v-if="showSearchField">
        <InputGroup class="mt-1">
          <InputText
            v-model:model-value="search"
            :placeholder="
              individualSearchText
                ? t('components.SearchList.inputSearchPlaceholderIndividual', { name })
                : t('components.SearchList.inputSearchPlaceholder')
            "
            :pt="{
              root: {
                'data-testid': 'search',
              },
            }"
            :autofocus="autofocus"
          />
          <LISAButton
            type="button"
            :label="t('components.SearchList.buttonOptionsText')"
            class="p-button-outlined p-button-secondary"
            @click="openOverlay"
          />
          <Popover ref="overlayRef" class="SearchListOverlayPanelScope">
            <div class="pb-2">
              {{ t("components.SearchList.DDHeadline") }}
            </div>
            <div class="make-everything-fit-into-one-row" style="transform: scale(0.8, 0.8)">
              <Checkbox v-model="searchOptionsFuzzy" binary />
              {{ t("components.SearchList.fuzzySearchTitle") }}
            </div>
            <hr />
            <div class="pb-2">{{ t("components.SearchList.searchFor") }}</div>
            <div>
              <div
                v-for="(c, cIndex) in checkbox"
                :key="`index${cIndex}`"
                class="pt-1"
                style="transform: scale(0.8, 0.8)"
              >
                <Checkbox
                  name="searchFor"
                  :model-value="selected"
                  :value="c.value"
                  @update:model-value="selectedUpdated"
                />
                {{ c.text }}
              </div>
            </div>
            <LISAButton
              class="mt-2"
              :label="t('global.download')"
              icon="far fa-file-excel"
              @click="exportAsXLSX"
            />
          </Popover>

          <div v-if="selectMode !== 'single'" class="padding-for-my-button">
            <LISAButton
              type="button"
              class="firstButton p-button-outlined p-button-secondary"
              @click="selectAll"
            >
              <i class="fas fa-check pr-2" />{{ t("components.SearchList.selectAll") }}
            </LISAButton>
            <LISAButton
              type="button"
              class="secondButton p-button-outlined p-button-secondary"
              @click="deselectAll"
            >
              <i class="fas fa-times pr-2" />{{ t("components.SearchList.selectNone") }}
            </LISAButton>
          </div>
        </InputGroup>

        <div v-show="!hideOptions" id="options" class="jumbotron py-2 px-3 mt-2">
          <h4 class="py-2">{{ t("components.SearchList.optionsText") }}</h4>
          <div class="form-group row">
            <div class="col-sm-2 pb-2">
              {{ t("components.SearchList.fuzzySearchTitle") }}
            </div>
            <div class="col-sm-10" style="transform: scale(0.8, 0.8)">
              <Checkbox v-model="searchOptionsFuzzy" binary />
            </div>
          </div>
          <div class="form-group row">
            <div class="col-sm-2 pb-2">
              {{ t("components.SearchList.searchFor") }}
            </div>
            <div class="col-sm-10" style="transform: scale(0.8, 0.8)">
              <Checkbox
                v-for="(option, index) in checkbox"
                :key="`checkbox${index}`"
                :model-value="selected"
                class="pt-1"
                :value="option.value"
                name="flavour-1"
                @update:model-value="selectedUpdated"
              />
            </div>
          </div>
        </div>
        <slot name="subline" :actual-length="value?.length" :filtered-length="filtered?.length">
          <p class="text-secondary mt-0" data-testid="searchItemLength">
            {{
              t("components.SearchList.outputSearchText2", {
                length: filtered?.length,
                name: name ? name : t(nameTC, filtered?.length),
              })
            }}
          </p>
        </slot>
      </div>
      <DataTable
        v-model:selection="selectedElements"
        v-model:sort-order="sortDescComputed"
        v-model:sort-field="defaultSortKeyComputed"
        :value="filtered"
        :paginator="paginator"
        :virtual-scroller-options="scrollable && !flexScrolling ? scrollOptions : undefined"
        :rows="perPage"
        :loading="loading"
        :selection-mode="disableSelection ? undefined : selectMode"
        :scroll-height="scrollable && !flexScrolling ? scrollHeight : 'flex'"
        :scrollable="scrollable"
        class="p-datatable-sm CustomSearchListClass table-lisa"
        :style="{
          maxHeight: maxHeight == null ? '100%' : `${maxHeight}`,
          minHeight: minHeight == null ? '1em' : `${minHeight}`,
          ...customStyle,
        }"
        :pt="{
          bodyRow: getBodyRowPT,
          emptyMessageCell: {},
        }"
        :meta-key-selection="false"
        :row-class="rowClassCombined"
        row-hover
        striped-rows
        @row-click="onRowClicked"
        @row-select="onRowSelected"
        @row-unselect="$emit('row-selected', null)"
      >
        <template #empty>
          <div class="w-100" style="text-align: center">{{ noResultMessageComputed }}</div>
        </template>
        <template v-if="$slots.header" #header>
          <slot name="header" />
        </template>
        <Column
          v-for="(attr, attrIndex) in attributes"
          :key="`attr${attrIndex}`"
          :field="attr.key"
          :sortable="attr.sortable ?? true"
          :pt="{
            root: {
              'data-testid': `column-${attr.key}`,
            },
          }"
          :header="attr.label"
          :class="`${attr.classList ?? ''} ${attr.fit ? 'column-fit' : 'column-grow'}`"
          :style="attr.cellStyle"
          header-class="tableHeader-format"
        >
          <template #body="data">
            <template v-if="customFields.includes(attr.key)">
              <slot :name="attr.key" :data="{ item: typeData(data.data) }" />
            </template>
            <span v-else :data-testid="attr.addTestId ? `cell-${data.data[attr.key]}` : undefined">
              {{ data.data[attr.key] }}
            </span>
          </template>
          <template v-if="`${attr.key}-header-leading` in $slots" #header="data">
            <slot :name="`${attr.key}-header-leading`" :data="{ item: data.column }" />
          </template>
        </Column>
      </DataTable>
    </main>
  </div>
</template>

<script setup lang="ts" generic="T extends Record<string, any>[], A extends ListAttributeType[]">
import { computed, ref, onMounted, watch } from "vue";
import Fuse from "fuse.js";
import { useStore } from "vuex";
import InputText from "primevue/inputtext";
import LISAButton from "@LISAPrime/LISAButton.vue";
import DataTable, {
  type DataTableProps,
  type DataTableRowClickEvent,
  type DataTableSortMeta,
} from "primevue/datatable";
import Column from "primevue/column";
import Checkbox from "primevue/checkbox";
import { useRouter, type RouteLocationRaw } from "vue-router";
import type { SearchListFilterCacheEntry } from "@/store/filterCache";
import usePropsAsDefault from "@/functions/usePropsAsDefault";
import Popover from "primevue/popover";
import InputGroup from "primevue/inputgroup";
import { useXLSX, type XLSXConfigPage } from "@/functions/useXLSXExport";
import { DateTime } from "luxon";
import type { ListAttributeType, SelectedKeys } from "./SearchListHelper";
import { useTranslation } from "@/language/translation";

const props = withDefaults(
  defineProps<{
    scrollHeight?: string;
    minHeight?: string;
    rowClass?: ((data: T[number]) => string | object | undefined) | undefined;
    smallPadding?: boolean;
    maxHeight?: number | string | null;
    sortDesc?: boolean;
    individualSearchText?: boolean;
    value: T;
    attributes: A;
    selected: SelectedKeys<T>;
    name?: string;
    nameTC?: string;
    detailRoute?: string | ((item: T[number]) => RouteLocationRaw | void);
    loading?: boolean;
    defaultSortKey?: string;
    multiSortMeta?: DataTableSortMeta[];
    showSearchField?: boolean;
    selectable?: boolean;
    perPage?: number;
    selectMode?: DataTableProps["selectionMode"];
    disableSelection?: boolean;
    localStorageKey?: null | { key: string; siteId?: number };
    autofocus?: boolean;
    scrollable?: boolean;
    flexScrolling?: boolean;
    scrollOptions?: {
      itemSize: number;
    };
    defaultFuzzySearch?: boolean;
    customStyle?: Record<string, string>;
    paginator?: boolean;
    dataTestKey?: string;
    noResultMessage?: string;
  }>(),
  {
    scrollHeight: "46ch",
    minHeight: "1em",
    rowClass: undefined,
    smallPadding: false,
    maxHeight: null,
    sortDesc: false,
    individualSearchText: false,
    name: "",
    nameTC: "",
    detailRoute: undefined,
    loading: false,
    defaultSortKey: "id",
    multiSortMeta: undefined,
    showSearchField: true,
    selectable: true,
    perPage: 15,
    selectMode: "single",
    disableSelection: false,
    localStorageKey: null,
    autofocus: false,
    scrollable: false,
    flexScrolling: false,
    defaultFuzzySearch: false,
    customStyle: () => ({}),
    paginator: false,
    dataTestKey: "id",
    noResultMessage: undefined,
    scrollOptions: () => ({
      itemSize: 46,
    }),
  },
);

const { t } = useTranslation();

const noResultMessageComputed = computed(() => props.noResultMessage ?? t("global.noResult"));

function rowClassCombined(item: T[number]): string | object | undefined {
  const classes: (string | object)[] = [];
  if (props.rowClass != null) {
    const result = props.rowClass(item);
    if (result != null) {
      classes.push(result);
    }
  }
  const possibleDate: string = item.updatedAt ?? item.createdAt;
  // check if the string is a ISO date
  if (
    possibleDate != null &&
    typeof possibleDate === "string" &&
    possibleDate.match(
      /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])(?:T([01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,3})?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)?)?$/,
    )
  ) {
    if (possibleDate && DateTime.fromISO(possibleDate).isValid) {
      const date = DateTime.fromISO(possibleDate);
      if (date.diffNow("days").days > -1) {
        classes.push("highlight-new");
      }
    }
  }
  return classes.length === 0 ? undefined : classes;
}

const emit = defineEmits<{
  "row-selected": [null | T[number] | T[number][]];
  rowClick: [DataTableRowClickEvent<T[number]>];
  searching: [{ state: boolean; keyword: string; selected: string[] }];
  selection: [T[number][]];
  "update:selected": [typeof props.selected];
}>();

function typeData(data: any): T[number] {
  return data as T[number];
}

const router = useRouter();
const store = useStore();
const selectedCache = ref(null as null | string[]);
const search = ref("");
const searchOptionsFuzzy = usePropsAsDefault({
  dynamicProps: computed(() => props.defaultFuzzySearch),
});
const hideOptions = ref(true);
const overlayRef = ref<InstanceType<typeof Popover>>();
const selectedElements = ref<T[number][]>([]);

const customFields = computed(() => {
  return props.attributes.filter((attr) => attr.custom || false).map((attr) => attr.key);
});

const filtered = computed(() => {
  if (search.value?.length < 1) {
    return props.value;
  }
  const fuse = new Fuse(props.value.map((el) => ({ ...el })) as T, {
    isCaseSensitive: false,
    shouldSort: true,
    threshold: !searchOptionsFuzzy.value ? 0 : 0.3,
    ignoreLocation: true,
    minMatchCharLength: 2,
    keys: selectedCache.value ?? props.selected,
  });
  return fuse.search(search.value).map((el) => el.item);
});

const checkbox = computed((): { text: string; value: string }[] => {
  return props.attributes
    .filter((attr) => attr.selectable)
    .map((attr) => {
      return {
        text: attr.label as string,
        value: attr.key,
      };
    });
});

const sortDescLocal = ref<number>();

const defaultSortKeyLocal = ref<string>();

function loadFilterCache() {
  if (props.localStorageKey != null) {
    const filterCache: SearchListFilterCacheEntry = store.getters[
      "filterCache/getSearchListFilter"
    ](props.localStorageKey.key, props.localStorageKey.siteId);
    if (filterCache != null) {
      search.value = filterCache.search;
      selectedCache.value = filterCache.selected;
      searchOptionsFuzzy.value = filterCache.searchOptionsFuzzy;
      sortDescLocal.value = filterCache.sortDesc;
      defaultSortKeyLocal.value = filterCache.defaultSortKey;
    }
  }
}

function setFilterCache() {
  if (props.localStorageKey != null) {
    const filterCache: SearchListFilterCacheEntry = {
      search: search.value,
      searchOptionsFuzzy: searchOptionsFuzzy.value,
      selected: props.selected,
      sortDesc: sortDescLocal.value,
      defaultSortKey: defaultSortKeyLocal.value,
    };
    store.dispatch("filterCache/updateSearchListFilter", {
      key: props.localStorageKey.key,
      siteId: props.localStorageKey.siteId,
      filterCache,
    });
  }
}

const sortDescComputed = computed({
  get() {
    return sortDescLocal.value ?? (props.sortDesc ? -1 : 1);
  },
  set(val: number) {
    sortDescLocal.value = val;
    setFilterCache();
  },
});

const defaultSortKeyComputed = computed({
  get() {
    return defaultSortKeyLocal.value ?? props.defaultSortKey;
  },
  set(val: string) {
    defaultSortKeyLocal.value = val;
    setFilterCache();
  },
});

function selectAll() {
  selectedElements.value = [...filtered.value];
}

function deselectAll() {
  selectedElements.value = [];
}

defineExpose({
  selectAll,
  deselectAll,
});

function emitSelected() {
  if (search.value?.length > 0) {
    emit("searching", { state: true, keyword: search.value, selected: props.selected });
  } else {
    emit("searching", { state: false, keyword: "", selected: props.selected });
  }
}

function onRowClicked(item: DataTableRowClickEvent<T[number]>) {
  emit("rowClick", item);
}

function onRowSelected(event: { data: { id?: number } | any[] }) {
  let id: undefined | number;
  if (Array.isArray(event.data) && event.data[0]?.id) {
    id = event.data[0]?.id;
  } else if ("id" in event.data) {
    id = event.data.id;
  }
  if (typeof props.detailRoute === "function") {
    const route = props.detailRoute(Array.isArray(event.data) ? event.data[0] : event.data);
    if (route != null) {
      router.push(route);
    } else {
      selectedElements.value = [];
    }
  } else if (props.detailRoute != null && props.detailRoute.length !== 0) {
    if (id != null) {
      router.push(
        `/${props.detailRoute[0] === "/" ? props.detailRoute.slice(1) : props.detailRoute}/${id}`,
      );
    }
  }
  emit("row-selected", event.data);
}

function openOverlay(event: Event) {
  if (overlayRef.value != null) {
    overlayRef.value.toggle(event);
  }
}

onMounted(() => {
  loadFilterCache();
});

watch(search, () => {
  emitSelected();
  setFilterCache();
  loadFilterCache();
});

watch(hideOptions, () => {
  emitSelected();
});

watch(
  computed(() => props.selected),
  () => {
    setFilterCache();
    loadFilterCache();
  },
);

watch(searchOptionsFuzzy, () => {
  setFilterCache();
  loadFilterCache();
});

watch(selectedElements, () => emit("selection", selectedElements.value), { deep: true });

function selectedUpdated(selectedArray: typeof props.selected) {
  emit("update:selected", selectedArray);
  setFilterCache();
  loadFilterCache();
}

function exportAsXLSX() {
  const downloadableAttrs = props.attributes.filter(
    (attr) => attr.downloadable == null || attr.downloadable,
  );
  const headers: XLSXConfigPage["headers"] = downloadableAttrs.map((attr) => ({
    label: attr.label,
    width: attr.label.length,
    key: attr.key,
  }));

  const xlsx = useXLSX(
    {
      fileName: `LISA_Data_Export_${props.name}_${DateTime.now().toFormat("yyyy-MM-dd")}`,
      pages: [
        {
          name: props.name,
          headers,
          data: props.value.map((el) =>
            downloadableAttrs.map((attr) => ({
              value:
                typeof el[attr.key] === "object" && el[attr.key] != null
                  ? JSON.stringify(el[attr.key])
                  : (el[attr.key]?.toString() ?? ""),
            })),
          ),
        },
      ],
      title: props.name,
    },
    true,
  );
  return xlsx.downloadAsync();
}

function getBodyRowPT(options: any) {
  return {
    "data-testid": `row-${options.props?.rowData?.[props.dataTestKey]}`,
  };
}
</script>

<style lang="scss" scoped>
.firstButton {
  border-radius: 0 !important;
}

.secondButton {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
</style>

<style lang="scss">
.CustomSearchListClass {
  .highlight-new td:first-of-type {
    border-left: 0.3rem var(--p-orange-500) solid !important;
  }
  .column-grow {
    width: auto;
  }
  .column-fit {
    width: 1%;
    white-space: nowrap;
  }
  td {
    vertical-align: top !important;
  }
}
.SearchListOverlayPanelScope {
  .p-overlaypanel-content {
    padding-left: 0;
    padding-right: 0;

    hr {
      margin-top: 1rem;
      margin-bottom: 1rem;
    }

    > div {
      margin-left: 1rem;
      margin-right: 1rem;
    }
  }
}
.p-datatable.p-datatable-sm .p-datatable-tbody > tr > td {
  vertical-align: top;
}
</style>
